import { useRef, useState } from "react";
import { Camera, Upload, X, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const fileUrl = (path) => `${API}/files/${path}`;

/** Unggah 1 file ke object storage persisten → path */
export async function uploadFile(file) {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

/** Foto bukti material: kamera HP (capture) atau upload; multi foto; thumbnail + hapus */
export function PhotoCapture({ photos, onChange, testid = "photo" }) {
  const camRef = useRef(null), upRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const add = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const paths = [];
      for (const f of Array.from(files)) {
        if (!/^image\//.test(f.type)) { toast.error(`${f.name}: bukan gambar`); continue; }
        paths.push(await uploadFile(f));
      }
      onChange([...photos, ...paths]);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`${testid}-capture`}>
      <div className="flex flex-wrap gap-2">
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`${testid}-camera-input`} />
        <input ref={upRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`${testid}-upload-input`} />
        <Button type="button" variant="outline" disabled={busy} onClick={() => camRef.current?.click()} className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid={`${testid}-camera-button`}><Camera className="mr-1.5 h-4 w-4" />Foto Kamera</Button>
        <Button type="button" variant="outline" disabled={busy} onClick={() => upRef.current?.click()} className="border-slate-700" data-testid={`${testid}-upload-button`}><Upload className="mr-1.5 h-4 w-4" />{busy ? "Mengunggah…" : "Upload Foto"}</Button>
        <span className="self-center text-[11px] text-slate-500">{photos.length ? `${photos.length} foto` : "Foto bukti material wajib"}</span>
      </div>
      {photos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testid}-thumbs`}>
          {photos.map((p) => (
            <div key={p} className="relative">
              <img src={fileUrl(p)} alt="bukti" className="h-20 w-20 rounded border border-slate-700 object-cover" />
              <button type="button" onClick={() => onChange(photos.filter((x) => x !== p))} className="absolute -right-1.5 -top-1.5 rounded-full bg-rose-600 p-0.5 text-white" data-testid={`${testid}-remove`}><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Galeri/preview foto bukti dengan info transaksi */
export function PhotoPreviewDialog({ item, onClose }) {
  const [i, setI] = useState(0);
  if (!item) return null;
  const photos = item.photos || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0D1524] border-slate-800" data-testid="photo-preview-dialog">
        <DialogHeader><DialogTitle className="font-display text-slate-100">Foto Bukti Material <span className="cn text-sm font-normal text-slate-500">材料凭证照片</span></DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400" data-testid="photo-preview-info">
          <span>Proyek: <b className="text-slate-200">{item.projectKode || ""} {item.projectName}</b></span>
          <span>Marking: <b className="font-mono text-amber-400">{item.code}</b></span>
          <span>Vendor: <b className="text-slate-200">{item.vendorName}</b></span>
          <span>Tanggal: <b className="font-mono text-slate-200">{item.at ? new Date(item.at).toLocaleString("id-ID") : item.tanggal}</b></span>
          <span>Qty pemeriksaan: <b className="font-mono text-emerald-300">{item.qty} pcs</b></span>
          {item.confirmed != null && <span>Konfirmasi: <b className={item.confirmed ? "text-emerald-300" : "text-rose-300"}>{item.confirmed ? "IYA" : "TIDAK"}</b></span>}
        </div>
        {photos.length === 0 ? <div className="py-10 text-center text-sm text-slate-500" data-testid="photo-preview-empty">Transaksi ini tidak memiliki foto (riwayat sebelum aturan Final Check)</div> : (
          <>
            <img src={fileUrl(photos[Math.min(i, photos.length - 1)])} alt="bukti" className="max-h-[60vh] w-full rounded border border-slate-800 object-contain" data-testid="photo-preview-main" />
            {photos.length > 1 && <div className="flex gap-2">{photos.map((p, k) => <img key={p} src={fileUrl(p)} alt="" onClick={() => setI(k)} className={`h-14 w-14 cursor-pointer rounded border object-cover ${k === i ? "border-amber-400" : "border-slate-700"}`} />)}</div>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Peringatan Yes/No progress/aktual melebihi target — keterangan wajib per marking */
export function OverTargetDialog({ rows, notes, onNotes, onYes, onNo, title = "Progress melebihi target alokasi" }) {
  if (!rows?.length) return null;
  const ok = rows.every((r) => (notes[r.markingId] || "").trim().length > 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onNo()}>
      <DialogContent className="max-w-2xl bg-[#0D1524] border-amber-500/40" data-testid="over-target-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-amber-300"><AlertTriangle className="h-5 w-5" />{title} <span className="cn text-sm font-normal text-slate-400">超出目标</span></DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Target tidak dinaikkan otomatis. Isi keterangan untuk setiap marking yang berlebih; persetujuan ini tidak menggantikan final check / QC.</p>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="py-1 pr-2">Marking</th><th className="py-1 pr-2 text-right">Target</th><th className="py-1 pr-2 text-right">Sebelumnya</th><th className="py-1 pr-2 text-right">Input</th><th className="py-1 pr-2 text-right">Total</th><th className="py-1 pr-2 text-right">Lebih</th><th className="py-1">Keterangan *</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.markingId} className="border-t border-slate-800" data-testid={`over-row-${r.code}`}>
                <td className="py-1 pr-2 font-mono text-amber-400">{r.code}</td><td className="py-1 pr-2 text-right font-mono">{r.target}</td><td className="py-1 pr-2 text-right font-mono text-slate-400">{r.before}</td>
                <td className="py-1 pr-2 text-right font-mono text-sky-300">{r.input}</td><td className="py-1 pr-2 text-right font-mono">{r.total}</td><td className="py-1 pr-2 text-right font-mono font-bold text-rose-300">+{r.over}</td>
                <td className="py-1"><Input value={notes[r.markingId] || ""} onChange={(e) => onNotes({ ...notes, [r.markingId]: e.target.value })} placeholder="alasan kelebihan…" className="h-8 border-amber-500/40 bg-slate-900 text-xs" data-testid={`over-note-${r.code}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onNo} className="border-slate-700" data-testid="over-target-no">No — kembali ke form</Button>
          <Button type="button" onClick={onYes} disabled={!ok} className="bg-amber-600 hover:bg-amber-500 text-[#0B1220] font-semibold disabled:opacity-40" data-testid="over-target-yes">Yes — simpan Qty aktual</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const StatusChip = ({ status }) => {
  const cls = status === "APPROVED" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : status === "SEBAGIAN APPROVED" ? "border-sky-500/40 bg-sky-500/10 text-sky-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300";
  const zh = status === "APPROVED" ? "已批准" : status === "SEBAGIAN APPROVED" ? "部分批准" : "待终检";
  return <span className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] ${cls}`}>{status} <span className="cn opacity-70">{zh}</span></span>;
};

export const TargetChip = ({ ts }) => {
  if (!ts) return null;
  const cls = ts.key === "TERPENUHI" ? "text-emerald-300" : ts.key === "LEBIH" ? "text-rose-300" : "text-amber-300";
  return <span className={`whitespace-nowrap font-mono text-[11px] ${cls}`}>{ts.label} <span className="cn opacity-70">{ts.zh}</span></span>;
};

export const Thumb = ({ photos, onClick, testid }) => photos?.length ? (
  <button type="button" onClick={onClick} className="relative" data-testid={testid}><img src={fileUrl(photos[0])} alt="" className="h-10 w-10 rounded border border-slate-700 object-cover" />{photos.length > 1 && <span className="absolute -right-1 -bottom-1 rounded bg-slate-800 px-1 text-[9px] text-slate-200">+{photos.length - 1}</span>}</button>
) : <button type="button" onClick={onClick} className="text-slate-600" title="Tanpa foto" data-testid={testid}><ImageIcon className="h-4 w-4" /></button>;
